import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import styled from 'styled-components';

import { useTranslation } from 'next-i18next';
import { MapHeaderContent } from '../components/map/MapHeaderContent';
import { MapButton } from '../components/map/MapButton';
import { NextPageProps } from '../typings/NextPageProps';
import { checkClient } from '../utils/serverSide';
import { Device, useDevice } from '../custom/useDevice';

const StyledContentContainer = styled.div`
	color: ${(props) => props.theme.black};
	font-family: 'Plus Jakarta Sans', sans-serif;
	padding: ${(props) => 16 / props.theme.fontSizeBase}rem 0 ${(props) => 32 / props.theme.fontSizeBase}rem 0;
	background: ${(props) => props.theme.menuBackground};
`;

const StyledContent = styled.div<{$isDesktop: boolean}>`
	padding: 0 ${(props) => 32 / props.theme.fontSizeBase}rem;
	margin: ${(props) => 16 / props.theme.fontSizeBase}rem auto 0 auto;
	white-space: pre-wrap;

	${(props) => props.$isDesktop && `
		width: 50%;
	`};

	h1 {
		font-style: normal;
		font-weight: 700;
		font-size: ${(props) => 27 / props.theme.fontSizeBase}rem;
		line-height: ${(props) => 24 / props.theme.fontSizeBase}rem;
		margin: 0 0 ${(props) => 40 / props.theme.fontSizeBase}rem 0;
	}

	h2 {
		font-style: normal;
		font-weight: 600;
		font-size: ${(props) => 20 / props.theme.fontSizeBase}rem;
		line-height: ${(props) => 24 / props.theme.fontSizeBase}rem;
		margin: 0 0 ${(props) => 16 / props.theme.fontSizeBase}rem 0;
	}

	p {
		font-style: normal;
		font-weight: 500;
		font-size: ${(props) => 14 / props.theme.fontSizeBase}rem;
		line-height: 170%;
		margin: 0 0 ${(props) => 16 / props.theme.fontSizeBase}rem 0;
	}

	ol, li {
		font-size: ${(props) => 14 / props.theme.fontSizeBase}rem;
	}
`;

const StyledButtonContainer = styled.div<{$isDesktop: boolean}>`
	display: flex;
	justify-content: center;
	width: 100%;

	button {
		padding: 0 ${(props) => 40 / props.theme.fontSizeBase}rem;
	}
`;

declare const openAxeptioCookies: () => void;

export const PolitiqueDeConfidentialite = ({ seo, client }: NextPageProps) => {
	const { t } = useTranslation(['common']);
	const device = useDevice();

	return (
		<>
			<NextSeo
				{...seo}

				title={t('common:privacyTitle') || seo.title}
				openGraph={{
					...seo.openGraph,
					title: t('common:privacyTitle') || seo.openGraph.title,
				}}
			/>
			<MapHeaderContent title={t('common:privacyTitle')} translation={client.attributes.translations}>
				<StyledContentContainer>
					<StyledContent
						$isDesktop={device === Device.DESKTOP || device === Device.TABLET_LANDSCAPE}
					>
						<h1>{ t('common:pageConfidentialiteTitle') }</h1>
						<p dangerouslySetInnerHTML={{
							__html: t('common:pageConfidentialiteText1') || '',
						}}/>

						<h2>{ t('common:pageConfidentialiteSubTitle2') }</h2>
						<p dangerouslySetInnerHTML={{
							__html: t('common:pageConfidentialiteText2') || '',
						}}/>

						<h2>{ t('common:pageConfidentialiteSubTitle3') }</h2>
						<p dangerouslySetInnerHTML={{
							__html: t('common:pageConfidentialiteText3') || '',
						}}/>

						<h2>{ t('common:pageConfidentialiteSubTitle4') }</h2>
						<p dangerouslySetInnerHTML={{
							__html: t('common:pageConfidentialiteText4') || '',
						}}/>

						<h2>{ t('common:pageConfidentialiteSubTitle5') }</h2>
						<p dangerouslySetInnerHTML={{
							__html: t('common:pageConfidentialiteText5') || '',
						}}/>

						<h2>{ t('common:pageConfidentialiteSubTitle6') }</h2>
						<p dangerouslySetInnerHTML={{
							__html: t('common:pageConfidentialiteText6') || '',
						}}/>
					</StyledContent>
					<StyledButtonContainer
						$isDesktop={device === Device.DESKTOP || device === Device.TABLET_LANDSCAPE}
					>
						<MapButton className="axeptio-link" onClick={() => { openAxeptioCookies(); }}>
							{t('common:buttonCookies')}
						</MapButton>
					</StyledButtonContainer>
				</StyledContentContainer>
			</MapHeaderContent>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = (context) => checkClient(context, ['classic', 'map'], ['common']);

export default PolitiqueDeConfidentialite;
